/**
 * A barra que aparece quando alguém da equipe está marcado.
 *
 * Papel: mostra quantas pessoas estão marcadas e as três ações sobre elas —
 * "🖨 Exportar PDF", "⇩ Exportar Excel" e "Limpar seleção" —, numa faixa
 * cinza entre o cabeçalho da seção "Equipe ativa" e a tabela.
 *
 * Como usar: quem monta a barra só a desenha com uma ou mais pessoas
 * marcadas; com zero, ela deverá sumir, e não aparecer com "0 colaborador
 * selecionado" e botões que exportariam um arquivo vazio. A barra fica colada
 * à borda de cima da tabela, sem margem embaixo, e a margem de cima se funde
 * com a de baixo do cabeçalho — acrescentar margem inferior aqui descolaria as
 * duas.
 *
 * A barra some na impressão (`print:hidden`): a página impressa pelo navegador
 * mostra a tabela, e não os controles sobre ela.
 */
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/lib/utils';
import { contagemDeSelecionados } from '../equipe-selecionada';

interface BarraDeSelecaoProps {
  quantidade: number;
  /** Chamado em "🖨 Exportar PDF". */
  onExportarPdf: () => void;
  /** Chamado em "⇩ Exportar Excel". */
  onExportarExcel: () => void;
  /** Chamado em "Limpar seleção". */
  onLimpar: () => void;
  /** Verdadeiro enquanto a planilha está sendo gerada, o que trava o botão dela. */
  gerandoPlanilha: boolean;
  className?: string;
}

/**
 * A barra de seleção da equipe.
 *
 * @param quantidade - Quantas pessoas estão marcadas, uma ou mais.
 * @param onExportarPdf - Chamado ao pedir a folha de impressão.
 * @param onExportarExcel - Chamado ao pedir a planilha.
 * @param onLimpar - Chamado ao desmarcar todo mundo de uma vez.
 * @param gerandoPlanilha - Verdadeiro entre o clique em "⇩ Exportar Excel" e o
 *   download; o botão fica desabilitado nesse meio-tempo, para o segundo
 *   clique não baixar o arquivo duas vezes.
 * @param className - Classes de quem chama, aplicadas depois das internas
 *   pelo `cn()` e por isso capazes de sobrescrevê-las.
 * @returns A faixa com a contagem à esquerda e os três botões à direita.
 */
export function BarraDeSelecao({
  quantidade,
  onExportarPdf,
  onExportarExcel,
  onLimpar,
  gerandoPlanilha,
  className,
}: BarraDeSelecaoProps) {
  return (
    <div
      className={cn(
        'mt-3 flex flex-wrap items-center gap-3 rounded-md border border-linha-forte bg-superficie-2 px-5 py-2.5 print:hidden',
        className,
      )}
    >
      <span className="mr-auto font-mono text-xs text-texto-medio">
        {contagemDeSelecionados(quantidade)}
      </span>
      <Button type="button" variant="outline" onClick={onExportarPdf}>
        🖨 Exportar PDF
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={onExportarExcel}
        disabled={gerandoPlanilha}
      >
        ⇩ Exportar Excel
      </Button>
      <Button type="button" variant="outline" onClick={onLimpar}>
        Limpar seleção
      </Button>
    </div>
  );
}
